import React, { Suspense, useState } from 'react';
import { Button, Tooltip } from 'antd';
import { Polygon } from 'geojson';
import { getValidatedPinLocation } from 'utils/validated-pin-location';
import styles from './DestintionAreaEditor.module.css';
import Loading from 'components/Loading';

const CMSMap = React.lazy(() => import('components/CMSMap/CMSMap'));

type DestinationAreaEditorProps = Readonly<{
    value: Polygon;
    onChange: (value: Polygon) => void;
    pinLocation: { lat: number; lon: number } | null;
    onPinLocationChange: (value: { lat: number; lon: number }) => void;
}>;

export function DestinationAreaEditor({
    value,
    onChange,
    pinLocation = null,
    onPinLocationChange,
}: DestinationAreaEditorProps) {
    const [editedArea, setEditedArea] = useState<Polygon | null>(null);
    const [editedPinLocation, setEditedPinLocation] = useState<{
        lat: number;
        lon: number;
    } | null>(null);
    const [isEditingArea, setIsEditingArea] = useState(false);
    const [isEditingPinLocation, setIsEditingPinLocation] = useState(false);

    const startEditingArea = () => {
        if (value) {
            setEditedArea(value);
            setIsEditingPinLocation(false);
            setIsEditingArea(true);
        }
    };

    const startEditingPinLocation = () => {
        if (!pinLocation) {
            return;
        }
        setEditedPinLocation(pinLocation);
        setIsEditingArea(false);
        setIsEditingPinLocation(true);
    };

    const saveArea = () => {
        setIsEditingArea(false);
        if (editedArea) {
            onChange(editedArea);
        }
    };

    const savePinLocation = () => {
        setIsEditingPinLocation(false);
        if (editedPinLocation && onPinLocationChange) {
            onPinLocationChange(editedPinLocation);
        }
    };

    return (
        <>
            <div className={styles.map__editButton}>
                {isEditingArea ? (
                    <Tooltip
                        title="You need to save the area before you can save the destination"
                        open
                    >
                        <Button onClick={saveArea} type="primary">
                            Save new area
                        </Button>
                    </Tooltip>
                ) : (
                    <Button onClick={startEditingArea}>Edit area</Button>
                )}
                {isEditingPinLocation ? (
                    <Tooltip
                        title="Click on the map to place the new destination pin before saving"
                        open
                    >
                        <Button onClick={savePinLocation} type="primary">
                            Save new location
                        </Button>
                    </Tooltip>
                ) : (
                    <Button onClick={startEditingPinLocation}>
                        Edit location
                    </Button>
                )}
            </div>
            {value ? (
                <div className={styles.map__inner}>
                    <Suspense fallback={<Loading />}>
                        <CMSMap
                            isEditingArea={isEditingArea ? 'interest' : null}
                            isEditingPinLocation={isEditingPinLocation}
                            highlightedAreaOfInterest={value}
                            highlightedSearchArea={value}
                            onAreaChanged={setEditedArea}
                            onPinLocationChanged={setEditedPinLocation}
                            pinLocation={getValidatedPinLocation(
                                isEditingPinLocation
                                    ? editedPinLocation
                                    : pinLocation,
                            )}
                        />
                    </Suspense>
                </div>
            ) : null}
        </>
    );
}
